// Does this session dispatch over `firebase/firestore/lite`? (#410, #207 U8)
//
// **It does not, by default.** `DEFAULT_ON` is `false`, so a teacher with
// nothing set dispatches through `firebase/firestore` and the SDK's own durable
// mutation queue is a second replayer underneath ours.
//
// The shape is `readStoreFlag.js`'s, for its reasons: `localStorage` because it
// has to be flippable in a console on a deployed build, an env var beside it
// because jest has no `localStorage` worth setting from outside the process,
// and not `import.meta.env`, which is a parse error under jest (#112).
//
//     localStorage.setItem('coda.liteWrites', 'on');   // then reload
//     localStorage.removeItem('coda.liteWrites');      // then reload
//
// and in tests:
//
//     npm run test:lite    # the `integration` project, every write over lite
//
// It is not a third pass in `scripts/test-arms.mjs`, which runs the two read
// arms; the default is not on this one.
//
// ── The committed-mutation flicker, and order across documents ─────────────
//
// The full SDK's cache holds a committed mutation until the watch stream
// echoes it (`hasCommittedMutations`). A lite `commit()` has no such cache, so
// an overlay that dropped the batch at `acknowledged` would show the row, then
// not, then again. `queue.confirmed()` holds an acknowledged batch, and
// `overlay.js` folds it until the store's receipt for the scope is newer than
// the acknowledgement. See `stillAwaitingEcho` in `src/data/overlay.js`.
// Running lite under jest needs `clearImmediate` and `markResourceTiming`, both lent to
// the jsdom realm in `test/integration/jsdomWithFetch.cjs`.
//
// Two more differences from the full SDK (#423):
//
//   * **Order across documents.** The full SDK sends this device's batches on
//     one write stream in the order they were handed over, so the core's
//     per-document FIFO is enough there. A lite `commit()` is one REST call with
//     no order against any other, and a batch parked `unreachable` retries on its
//     own clock — so a charge saved offline and the invoice request after it
//     could land the wrong way round, and the invoice be generated without the
//     charge. The queue takes the core's studio-order rule while lite
//     dispatches (`readyBatchesInStudioOrder`, argued in `core/src/queue.rs`),
//     and a batch lite cannot *build* is transient rather than parked, so it
//     ends in a dead letter instead of holding the studio behind it for ever.
//   * **A sibling tab.** The full SDK's multi-tab mutation queue wakes every
//     other tab's listeners for a write made in one, offline included. A lite
//     write wakes nobody until the server echoes it, so `writes.js` posts each
//     queue transition to sibling tabs while lite dispatches.
//
// ── The coupling ────────────────────────────────────────────────────────────
//
// #410: **lite dispatch and the snapshot arm cannot both be on.** Lite writes
// over REST and enters no mutation queue, so `hasLocalMutations` is never true.
// The store arm does not care — its optimistic read is our
// queue folded through `overlay.js`. The snapshot arm has no overlay at all:
// its row and its `pendingWrite` are `d.data()` and
// `d.metadata.hasPendingWrites` straight off the listener
// (`useCollectionWindow.js`, and `test/ui/rollbackArmPendingWrite.test.jsx`
// holds each arm to the supplier it really has). So with `coda.readStore` set
// to `'off'`, an offline save would close the drawer on an unchanged row
// (#237). So:
//
//     the read store off ⇒ lite writes off, whatever this flag says.
//
// That is `liteWritesEnabled()` below. Rolling the read path back takes the
// write path back with it, in the same reload.

import { readStoreEnabled } from './readStoreFlag';

const KEY = 'coda.liteWrites';

// As in `readStoreFlag.js`: the browser's switch stays the browser's switch,
// and this is the one a test process can set.
const ENV_KEY = 'CODA_LITE_WRITES';

/**
 * A test's answer, when it has one. `null` means "ask the browser".
 *
 * An override rather than a `localStorage` write because jsdom's storage is
 * shared across a file's tests and a leaked `setItem` would be a flag one test
 * set and another inherited.
 */
let override = null;

/** Force the answer. `null` hands it back to `localStorage`. Tests, and only tests. */
export function __setLiteWritesEnabled(value) {
  override = value;
}

/**
 * The default, as one named constant rather than a `=== 'on'` buried in a
 * comparison.
 *
 * **`false`** (#423). One property of lite dispatch bears on it: a permanent
 * HTTP 500 arrives under the same code as an unreachable server, so it is
 * retried for as long as it lasts, and under the studio-order rule it holds
 * the studio rather than one document (`classifyCommitError` in
 * `liteDispatch.js`).
 */
export const DEFAULT_ON = false;

/** What this flag alone says, before the coupling below is applied. */
function requested() {
  if (override !== null) return override;
  try {
    const env = globalThis.process?.env?.[ENV_KEY];
    if (env === 'on') return true;
    if (env === 'off') return false;
  } catch {
    // No `process`, or one that refuses `env`. Fall through to the browser.
  }
  try {
    const stored = globalThis.localStorage?.getItem(KEY);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
  } catch {
    // Private mode, or a browser configured to refuse site data: `getItem`
    // itself throws. Not an error worth reporting — it is an answer.
    return DEFAULT_ON;
  }
  return DEFAULT_ON;
}

/** Said once per session, and only when the coupling actually took something away. */
let warnedAboutRollback = false;

/**
 * Are this session's writes dispatched over `firebase/firestore/lite`?
 *
 * Precedence, most specific first: a test's override, the environment, the
 * browser's `localStorage`, the default — **and then the read store's answer,
 * which outranks all four in one direction only.** It can turn this off and can
 * never turn it on: the snapshot arm with no mutation queue under it is #237,
 * while the store arm with the SDK's mutation queue under it is the default
 * arrangement.
 *
 * Read per dispatch rather than captured, unlike `readStoreEnabled()`. A read
 * arm decides which of two subscriptions a mounted provider is holding, so it
 * cannot move under one; this decides which SDK one `commit()` goes out on, and
 * every dispatch is independent. A batch already enqueued is replayed by
 * whichever dispatcher the replaying session has, so after a rollback a write
 * queued under lite goes out through the full SDK, enters its mutation queue,
 * and appears on the snapshot arm.
 */
export function liteWritesEnabled() {
  const want = requested();
  if (!want) return false;
  if (readStoreEnabled()) return true;
  if (!warnedAboutRollback) {
    warnedAboutRollback = true;
    console.warn(
      '[writes] `coda.liteWrites` is on and `coda.readStore` is off, so writes are being '
      + 'dispatched through `firebase/firestore` rather than `firebase/firestore/lite` '
      + '(#410). The snapshot read arm takes a teacher\'s own unlanded write from the SDK\'s '
      + 'mutation queue, which a lite dispatch never enters — so the two together would close '
      + 'a drawer on an unchanged row (#237). Turning the read store back on restores the lite '
      + 'dispatch; nothing else has to be set.',
    );
  }
  return false;
}

/** Tests only: forget that the warning above has been said. */
export function __resetLiteWriteWarning() {
  warnedAboutRollback = false;
}
